import { useNavigate } from "react-router-dom";
import {
  // #198 — icons for the leftover template header buttons, commented out
  // alongside the buttons below (may be restored later):
  // Search,
  // Coffee,
  // MessageSquareCode,
  // Pin,
  // ClipboardList,
  User,
  Settings,
  LogOut,
  Sun,
  Moon,
} from "lucide-react";
import { QuickAddMenu } from "../quick-add/QuickAddMenu";
import { NotificationsSheet } from "@/modules/core/notifications/NotificationsSheet";
// #198 — only used by the commented-out leftover header buttons (may be restored):
// import { Button } from "@marine/ui/button";
// import { Input, InputWrapper } from "@marine/ui/input";
// import { useLayout } from "./context";
import { getInitials } from "@/lib/helpers";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  AvatarIndicator,
  AvatarStatus,
} from '@marine/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@marine/ui/dropdown-menu';
import { useTheme } from "next-themes";
import { useTenantLogout, useEmployee } from "@/modules/tenant/hooks/useTenantAuth";
import { useUIPreference } from "@/hooks/useUIPreference";

interface HeaderToolbarProps {
  onLogout?: () => void;
  isLogoutPending?: boolean;
  portalType?: 'platform' | 'tenant';
}

export function HeaderToolbar({ onLogout, isLogoutPending, portalType = 'tenant' }: HeaderToolbarProps) {
  // #198 — only used by the commented-out leftover header buttons (may be restored):
  // const { isMobile } = useLayout();
  const { theme, setTheme } = useTheme();
  const [, setStoredTheme] = useUIPreference<'light' | 'dark' | 'system'>('theme', 'system');
  const defaultLogoutMutation = useTenantLogout();
  const employee = useEmployee();
  
  const navigate = useNavigate();
  // #198 — header search commented out (no-op handler); may be restored:
  // const handleInputChange = () => {};
  
  const toggleTheme = () => {
    const next: 'light' | 'dark' = theme === "light" ? "dark" : "light";
    setTheme(next);
    setStoredTheme(next);
  };

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      defaultLogoutMutation.mutate();
    }
  };

  const isPending = isLogoutPending ?? defaultLogoutMutation.isPending;
  const displayName =
    employee?.full_name ||
    employee?.name ||
    `${employee?.first_name ?? ''} ${employee?.last_name ?? ''}`.trim() ||
    employee?.email ||
    'User';
  const displayRole = employee?.role || 'User';
  const avatarSrc = employee?.avatar_url || undefined;
  const initials = getInitials(displayName, 2) || 'U';

  return (
    <nav className="flex items-center gap-2.5">
      {/*
        #198 — leftover template header buttons removed from the UI: they were
        non-functional (decorative icons + a no-op search + an unwired Reports
        button). Kept here, commented out, so they can be restored if needed.
        Reports remain available via the sidebar nav (/inventory/reports).

        <Button mode="icon" variant="outline"><Coffee /></Button>
        <Button mode="icon" variant="outline"><MessageSquareCode /></Button>
        <Button mode="icon" variant="outline"><Pin /></Button>

        {!isMobile && (
          <InputWrapper className="w-full lg:w-40">
            <Search />
            <Input type="search" placeholder="Search" onChange={handleInputChange} />
          </InputWrapper>
        )}

        {isMobile ? (
          <Button variant="outline" mode="icon"><ClipboardList /></Button>
        ) : (
          <Button variant="outline"><ClipboardList /> Reports</Button>
        )}
      */}
      <QuickAddMenu portalType={portalType} />

      {portalType === 'tenant' && <NotificationsSheet />}

      {/* User Dropdown Menu */}
      <DropdownMenu>
        <DropdownMenuTrigger className="cursor-pointer">
          <Avatar className="size-7">
            {avatarSrc && <AvatarImage src={avatarSrc} alt={displayName} />}
            <AvatarFallback>{initials}</AvatarFallback>
            <AvatarIndicator className="-end-2 -top-2">
              <AvatarStatus variant="online" className="size-2.5" />
            </AvatarIndicator>
          </Avatar>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56" side="bottom" align="end" sideOffset={11}>
          {/* User Information Section */}
          <div className="flex items-center gap-3 px-3 py-2">
            <Avatar>
              {avatarSrc && <AvatarImage src={avatarSrc} alt={displayName} />}
              <AvatarFallback>{initials}</AvatarFallback>
              <AvatarIndicator className="-end-1.5 -top-1.5">
                <AvatarStatus variant="online" className="size-2.5" />
              </AvatarIndicator>
            </Avatar>
            <div className="flex flex-col items-start">
              <span className="text-sm font-semibold text-foreground">{displayName}</span>
              <span className="text-xs text-muted-foreground">{displayRole}</span>
            </div>
          </div>
          
          <DropdownMenuSeparator />

          {/* User Actions */}
          <DropdownMenuItem onClick={() => navigate('/profile')}>
            <User/>
            <span>Profile</span>
          </DropdownMenuItem>

          <DropdownMenuItem onClick={() => navigate('/profile/preferences')}>
            <Settings/>
            <span>Settings</span>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/* Theme Toggle */}
          <DropdownMenuItem onClick={toggleTheme}>
            {theme === "light" ? <Moon className="size-4" /> : <Sun className="size-4" />}
            <span>{theme === "light" ? "Dark mode" : "Light mode"}</span>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {/* Action Items */}
          <DropdownMenuItem 
            onClick={handleLogout}
            disabled={isPending}
          >
            <LogOut/>
            <span>{isPending ? 'Signing out...' : 'Sign out'}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </nav>
  );
}
